/**
 * PostHog feature-flag registry.
 *
 * Add a flag here and it becomes available as `useFeatureFlags().<key>` — no other wiring
 * needed. This is the successor to Harness FME's `useToolBoxTreatments` (see that file's
 * `ToolBox_*` registry for the pattern being migrated away from); new flags belong here.
 *
 * One PostHog project serves every environment (VITE_PUBLIC_POSTHOG_KEY lives in the shared
 * .env), so each flag has two keys in PostHog — "<name>_staging" and "<name>_production" — and
 * the suffix is resolved from VITE_ENV at read time. Register the base name only.
 */
export const FEATURE_FLAGS = {
    showQuoteAttachments: 'use_quote_attachments',
    showGeneratedDocuments: 'use_generated_documents',
    showQuoteEmailDocuments: 'use_quote_email_documents',
    // Named "hide" so an unloaded or blocked PostHog (read as false) leaves these sections shown.
    hideZapierGuessZap: 'hide_zapier_guess_zap',
    hideZapierManageZaps: 'hide_zapier_manage_zaps',
}

/**
 * getFlagEnvironment - The flag-name suffix for the current build.
 *
 * A function rather than a module-level constant so `vi.stubEnv('VITE_ENV', ...)` works in
 * tests — a constant would freeze the value at import time.
 *
 * @returns {'staging' | 'production'} 'production' for production builds; 'staging' for
 *   staging and local development.
 */
export const getFlagEnvironment = () => (import.meta.env.VITE_ENV === 'production' ? 'production' : 'staging')

/**
 * resolveFlagName - Builds the environment-suffixed PostHog flag key for a registered base name.
 *
 * @param {string} baseName - A value from FEATURE_FLAGS, e.g. 'use_quote_attachments'.
 * @returns {string} The flag key as configured in PostHog, e.g. 'use_quote_attachments_staging'.
 */
export const resolveFlagName = (baseName) => `${baseName}_${getFlagEnvironment()}`
